import {ClassStatusExpansion, Contact, SchoolClass, ScoreFactor} from "models"
import {Header, Row, Table} from "tabloom"
import {memo, shapeComponent, ShapeComponent, Text, View} from "shared/base"
import ApiMakerConfig from "@kaspernj/api-maker/build/config"
import AppHistory from "shared/history"
import Button from "components/chalky-layout/button"
import DatePicker from "components/chalky-layout/date-picker"
import {digg} from "diggerize"
import EventEmitter from "events"
import I18n from "shared/i18n"
import Icon from "@kaspernj/api-maker/build/utils/icon"
import {incorporate} from "incorporator"
import {loading} from "components/layout/loading-blocker"
import moment from "shared/moment"
import Params from "@kaspernj/api-maker/build/params"
import ProUpgradeOverlay from "components/pro/upgrade-overlay"
import React from "react"
import Routes from "shared/routes"
import SchoolGroupRow from "./class-status/school-group-row"
import setLayout from "shared/set-layout"
import {sortedByFunction} from "sorted-by"
import {StyleSheet} from "react-native"
import useAnythingPro from "components/subscriptions/use-anything-pro"
import useCurrentUser from "@kaspernj/api-maker/build/use-current-user"
import {useFrontLayout} from "components/front-layout"
import useI18n from "i18n-on-steroids/build/src/use-i18n.js"
import useQueryParams from "on-location-changed/build/use-query-params"

const styles = StyleSheet.create({
  header: {
    justifyContent: "center",
    padding: 14,
    backgroundColor: "#fff",
    borderColor: "#e5dfff",
    borderStyle: "solid",
    borderTopWidth: 1
  },
  headerText: {
    color: "#87a2d5",
    fontSize: 12,
    fontWeight: 500
  }
})

export {styles}

/** @typedef {object} RoutesUserClassStatusProps */

/**
 * @typedef {object} RoutesUserClassStatusState
 * @property {unknown} data
 * @property {unknown} expanded
 * @property {unknown} schoolClassIds
 * @property {unknown} schoolGroupIds
 * @property {unknown} schoolIds
 */

/**
 * @extends {ShapeComponent<RoutesUserClassStatusProps, RoutesUserClassStatusState>}
 */
class RoutesUserClassStatus extends ShapeComponent {
  /** @type {RoutesUserClassStatusState} */
  state = {
    data: null,
    expanded: null,
    schoolClassIds: null,
    schoolGroupIds: null,
    schoolIds: null
  }

  events = new EventEmitter()

  setup() {
    setLayout("user")

    const {anythingPro} = useAnythingPro()
    const currentUser = useCurrentUser()
    const {locale, t} = useI18n({namespace: "js.routes.user.class_status"})
    const queryParams = useQueryParams()

    const dateFrom = React.useMemo(() => {
      if (queryParams.date_from) {
        return moment(queryParams.date_from).toDate()
      } else {
        return moment().startOf("week").toDate()
      }
    }, [queryParams.date_from])

    const dateTo = React.useMemo(() => {
      if (queryParams.date_to) {
        return moment(queryParams.date_to).toDate()
      } else {
        return moment().endOf("week").toDate()
      }
    }, [queryParams.date_to])

    this.anythingPro = anythingPro
    this.currentUser = currentUser
    this.dateFrom = dateFrom
    this.dateTo = dateTo
    this.t = t


    React.useMemo(() => {
      if (anythingPro) {
        this.loadSchoolGroups()
      }
    }, [anythingPro])

    const headerRight = React.useMemo(() => this.headerRight(), [dateFrom, dateTo, locale])

    useFrontLayout()?.setState({
      active: "user-class-status",
      centered: false,
      headerRight,
      headerTitle: t(".my_status"),
      userMenu: false,
      weekBar: false
    })

    React.useMemo(() => {
      if (this.s.schoolGroupIds) {
        this.loadData()
      }
    }, [dateFrom, dateTo])

    React.useMemo(() => {
      if (currentUser) {
        this.loadExpanded()
      }
    }, [currentUser?.id()])
  }

  loadExpanded = async () => {
    const classStatusExpansions = await ClassStatusExpansion.ransack({user_id_eq: this.tt.currentUser.id()}).toArray()
    const newExpanded = {}

    for (const classStatusExpansion of classStatusExpansions) {
      newExpanded[classStatusExpansion.identifier()] = classStatusExpansion.expanded()
    }

    this.setState((prevState) => ({expanded: Object.assign({}, prevState.expanded, newExpanded)}))
  }

  headerRight() {
    const {dateFrom, dateTo, t} = this.tt

    return (
      <View style={this.headerRightContainerStyle ||= {flexDirection: "row", alignItems: "center"}}>
        <View style={this.datePickerViewStyle ||= {marginRight: 10}}>
          <DatePicker dateFrom={dateFrom} dateTo={dateTo} onSelect={this.tt.onDatePickerSelect} testID="date-picker" />
        </View>
        <Button
          icon="plus"
          iconPosition="left"
          purple
          small
          title={t(".add_class")}
          to={Routes.userCreateClassStep1Path({back_to: "userClassStatus"})}
        />
      </View>
    )
  }

  async loadSchoolGroups() {
    await loading(this.t(".loading_schools"), async () => {
      const schoolGroups = await Contact
        .ransack({
          contact_type_identifier_eq: "school_group",
          s: "current_translation_name"
        })
        .preload([
          "children_relationships.contact_relationship_type",
          "children_relationships.child.school"
        ])
        .groupBy("id")
        .select({
          Contact: ["id", "name"],
          ContactRelationship: ["childId", "id"],
          ContactRelationshipType: ["identifier"],
          School: ["displayName", "id"]
        })
        .toArray()

      const schoolClasses = await SchoolClass
        .ransack({
          deleted_at_null: 1,
          s: "contact_current_translation_name",
          where_user_related_through_anything: JSON.stringify({user_contact_id: this.tt.currentUser.contactId()})
        })
        .accessibleBy("classStatusData")
        .abilities({
          SchoolClass: ["edit", "softDelete"]
        })
        .select({
          ClassStep: ["id", "name", "primarySchoolStep"],
          School: ["displayName", "id"],
          SchoolClass: ["classStepId", "id", "interpretedLegalName", "interpretedName", "schoolId"],
          User: ["id"]
        })
        .groupBy("id")
        .preload([
          "class_step",
          "school",
          "teachers"
        ])
        .toArray()

      const schoolIds = new Set()
      const schoolClassesBySchoolId = {}
      const schoolsById = {}
      const schoolClassIds = new Set()

      for (const schoolClass of schoolClasses) {
        const school = schoolClass.school()

        if (!school) continue

        schoolsById[school.id()] = school
        schoolIds.add(school.id())
        schoolClassIds.add(schoolClass.id())
        schoolClassesBySchoolId[school.id()] ||= []
        schoolClassesBySchoolId[school.id()].push(schoolClass)
      }

      this.schoolClassesBySchoolId = schoolClassesBySchoolId
      this.schoolClassIds = Array.from(schoolClassIds)
      this.schoolIds = Array.from(schoolIds)
      this.schoolsById = schoolsById
      this.setTeachersForSchoolGroups(schoolGroups)
    })
  }

  render() {
    const {anythingPro, events, t} = this.tt
    const {data} = this.s
    const schoolGroupDatas = data && Object.values(data.schoolGroups)

    return (
      <View testID="routes/user/class-status-route">
        {anythingPro === false &&
          <ProUpgradeOverlay onRequestClose={this.tt.onUpgradeOverlayRequestClose} />
        }
        <Table style={this.tableStyle ||= {minWidth: 1500}}>
          <Row>
            <Header
              style={this.nameHeaderStyle ||= [styles.header, {
                borderLeftWidth: 1,
                borderTopLeftRadius: 24
              }]}
              width={260}
            >
              <Icon name="chevron-up" style={styles.headerText} />
            </Header>
            <Header style={styles.header} width={200}>
              <Text style={styles.headerText}>{t(".nickname")}</Text>
            </Header>
            <Header style={styles.header}>
              <Text style={styles.headerText}>{t(".teachers")}</Text>
            </Header>
            <Header style={styles.header}>
              <Text style={styles.headerText}>{t(".top_positives")}</Text>
            </Header>
            <Header style={styles.header}>
              <Text style={styles.headerText}>{t(".top_negatives")}</Text>
            </Header>
            <Header style={styles.header}>
              <Text style={styles.headerText}>{t(".average_mood")}</Text>
            </Header>
            <Header style={styles.header}>
              <Text style={styles.headerText}>{t(".latest_mood_pure")}</Text>
            </Header>
            <Header style={styles.header}>
              <Text style={styles.headerText}>{t(".woofs")}</Text>
            </Header>
            <Header style={styles.header}>
              <Text style={styles.headerText}>{t(".brain_breaks")}</Text>
            </Header>
            <Header style={styles.header}>
              <Text style={styles.headerText}>{t(".challenges")}</Text>
            </Header>
            <Header style={styles.header}>
              <Text style={styles.headerText}>{t(".unicorn_effects")}</Text>
            </Header>
            <Header style={styles.header}>
              <Text style={styles.headerText}>{t(".active_courses")}</Text>
            </Header>
            <Header
              style={this.activeCoursesHeaderStyle ||= [styles.header, {
                borderRightWidth: 1,
                borderTopRightRadius: 24
              }]}
              width={50}
            >
              <Icon name="gears" style={styles.headerText} />
            </Header>
          </Row>
          {this.s.expanded !== null && schoolGroupDatas?.map((schoolGroupData, schoolGroupIndex) =>
            <SchoolGroupRow
              classStatusRoute={this}
              events={events}
              key={schoolGroupData.schoolGroup?.id() || "no-school-group"}
              lastRow={(schoolGroupIndex + 1) == schoolGroupDatas.length}
              schoolGroupData={schoolGroupData}
            />
          )}
        </Table>
      </View>
    )
  }

  setTeachersForSchoolGroups(schoolGroups) {
    const data = {
      schoolGroups: {}
    }

    const {schoolClassIds, schoolIds} = this.tt
    const schoolsWithSchoolGroup = {}

    for (const schoolGroup of schoolGroups) {
      const schoolGroupData = {
        activeCourses: null,
        brainBreaksCount: null,
        challengesCount: null,
        checkInsCount: null,
        latestMoods: null,
        moods: null,
        schoolGroup,
        schoolGroupId: schoolGroup.id(),
        schools: {},
        teachers: {},
        topNegativeScoreFactor: null,
        topPositiveScoreFactor: null,
        unicornEffectsCount: null
      }

      data.schoolGroups[schoolGroup.id()] = schoolGroupData

      const schools = this.schoolsForGroup(schoolGroup)

      for (const school of schools) {
        schoolsWithSchoolGroup[school.id()] = school
      }

      this.addSchoolsToSchoolGroupData({schoolGroupData, schools})
    }

    const schoolGroupIds = Object.keys(data.schoolGroups)
    const schoolsWithoutGroup = Object.values(this.schoolsById).filter((school) => !schoolsWithSchoolGroup[school.id()])

    if (schoolsWithoutGroup.length > 0) {
      const schoolGroupData = {
        activeCourses: null,
        brainBreaksCount: null,
        challengesCount: null,
        checkInsCount: null,
        latestMoods: null,
        moods: null,
        schoolGroup: null,
        schoolGroupId: "no-school-group",
        schools: {},
        teachers: {},
        topNegativeScoreFactor: null,
        topPositiveScoreFactor: null,
        unicornEffectsCount: null
      }

      data.schoolGroups["no-school-group"] = schoolGroupData

      this.addSchoolsToSchoolGroupData({schoolGroupData, schools: schoolsWithoutGroup})
    }

    this.setState({data, schoolClassIds, schoolGroupIds, schoolIds})
    this.loadData()
  }

  addSchoolsToSchoolGroupData({schoolGroupData, schools}) {
    const {schoolClassIds, schoolIds} = this.tt

    for (const school of schools) {
      const schoolId = school.id()
      const schoolClasses = this.schoolClassesBySchoolId[schoolId] || []

      const schoolData = {
        activeCourses: null,
        brainBreaksCount: null,
        challengesCount: null,
        checkInsCount: null,
        classSteps: {},
        latestMoods: null,
        moods: null,
        school,
        schoolId,
        teachers: {},
        topNegativeScoreFactor: null,
        topPositiveScoreFactor: null,
        unicornEffectsCount: null
      }

      if (!schoolIds.includes(schoolId)) schoolIds.push(schoolId)

      schoolGroupData.schools[schoolId] = schoolData

      for (const schoolClass of schoolClasses) {
        const classStep = schoolClass.classStep()
        const classStepId = classStep?.id() || "no-class-step"

        if (!schoolClassIds.includes(schoolClass.id())) schoolClassIds.push(schoolClass.id())

        if (!(classStepId in schoolData.classSteps)) {
          schoolData.classSteps[classStepId] = {
            activeCourses: null,
            brainBreaksCount: null,
            challengesCount: null,
            checkInsCount: null,
            classStep,
            classStepId,
            latestMoods: null,
            moods: null,
            schoolClasses: {},
            teachers: {},
            topNegativeScoreFactor: null,
            topPositiveScoreFactor: null,
            unicornEffectsCount: null
          }
        }

        const classStepData = schoolData.classSteps[classStepId]
        const teachers = this.teachersForSchoolClass(schoolClass)
        const schoolClassData = {
          activeCourses: null,
          brainBreaksCount: null,
          challengesCount: null,
          checkInsCount: null,
          latestMoodAverage: null,
          moodAverage: null,
          moods: null,
          schoolClass,
          teachers: {},
          topNegativeScoreFactor: null,
          topPositiveScoreFactor: null,
          unicornEffectsCount: null
        }

        classStepData.schoolClasses[schoolClass.id()] = schoolClassData

        for (const teacher of teachers) {
          classStepData.teachers[teacher.id()] = teacher
          schoolClassData.teachers[teacher.id()] = teacher
          schoolData.teachers[teacher.id()] = teacher
          schoolGroupData.teachers[teacher.id()] = teacher
        }
      }
    }
  }

  getScoreFactor(id) {
    if (id) {
      return digg(this.scoreFactorsById, id)
    }
  }

  async loadData() {
    await loading(this.t(".loading_data"), async () => {
      const {schoolClassIds, schoolGroupIds, schoolIds} = this.s
      const calculations = await SchoolClass.classStatusData({
        date_from: I18n.strftime("%Y-%m-%d", this.tt.dateFrom),
        date_to: I18n.strftime("%Y-%m-%d", this.tt.dateTo),
        school_ids: schoolIds,
        school_class_ids: schoolClassIds,
        school_group_ids: schoolGroupIds
      })
      const scoreFactorIds = digg(calculations, "score_factor_ids")
      const scoreFactors = await ScoreFactor
        .ransack({id_eq_any: scoreFactorIds})
        .select({ScoreFactor: ["id", "name"]})
        .toArray()

      this.scoreFactorsById = {}

      for (const scoreFactor of scoreFactors) {
        this.scoreFactorsById[scoreFactor.id()] = scoreFactor
      }

      const data = incorporate({}, this.s.data)

      for (const schoolGroupContactId in data.schoolGroups) {
        const schoolGroupData = data.schoolGroups[schoolGroupContactId]
        const schoolGroupCalculations = digg(calculations, "school_groups_data", schoolGroupContactId)

        schoolGroupData.activeCourses = 0
        schoolGroupData.brainBreaksCount = 0
        schoolGroupData.challengesCount = 0
        schoolGroupData.checkInsCount = 0
        schoolGroupData.latestMoods = []
        schoolGroupData.moods = []
        schoolGroupData.schoolGroupId = schoolGroupContactId
        schoolGroupData.topNegativeScoreFactor = this.getScoreFactor(digg(schoolGroupCalculations, "top_negative_score_factor"))
        schoolGroupData.topPositiveScoreFactor = this.getScoreFactor(digg(schoolGroupCalculations, "top_positive_score_factor"))
        schoolGroupData.unicornEffectsCount = 0

        for (const schoolId in schoolGroupData.schools) {
          const schoolData = schoolGroupData.schools[schoolId]
          const school = schoolData.school
          const schoolCalculations = digg(calculations, "schools_data", school.id())

          schoolData.activeCourses = 0
          schoolData.brainBreaksCount = 0
          schoolData.challengesCount = 0
          schoolData.checkInsCount = 0
          schoolData.latestMoods = []
          schoolData.moods = []
          schoolData.topNegativeScoreFactor = this.getScoreFactor(digg(schoolCalculations, "top_negative_score_factor"))
          schoolData.topPositiveScoreFactor = this.getScoreFactor(digg(schoolCalculations, "top_positive_score_factor"))
          schoolData.unicornEffectsCount = 0

          for (const classStepId in schoolData.classSteps) {
            const classStepData = schoolData.classSteps[classStepId]
            const classStepCalculations = digg(calculations, "schools_data", school.id(), "class_steps_data", classStepId)

            classStepData.activeCourses = 0
            classStepData.brainBreaksCount = 0
            classStepData.challengesCount = 0
            classStepData.checkInsCount = 0
            classStepData.latestMoods = []
            classStepData.moods = []
            classStepData.topNegativeScoreFactor = this.getScoreFactor(digg(classStepCalculations, "top_negative_score_factor"))
            classStepData.topPositiveScoreFactor = this.getScoreFactor(digg(classStepCalculations, "top_positive_score_factor"))
            classStepData.unicornEffectsCount = 0

            for (const schoolClassId in classStepData.schoolClasses) {
              const schoolClassData = classStepData.schoolClasses[schoolClassId]
              const calculationsForSchoolClass = digg(calculations, "class_status_data", schoolClassId)
              const activeCourses = digg(calculationsForSchoolClass, "active_courses")
              const brainBreaksCount = digg(calculationsForSchoolClass, "brain_breaks_this_week")
              const challengesCount = digg(calculationsForSchoolClass, "challenges_this_week")
              const checkInsCount = digg(calculationsForSchoolClass, "woofs_this_week")
              const latestMoodAverage = digg(calculationsForSchoolClass, "latest_mood_average")
              const moodAverage = digg(calculationsForSchoolClass, "mood_average")
              const unicornEffectsCount = digg(calculationsForSchoolClass, "unicorn_effects_this_week")

              schoolClassData.activeCourses = activeCourses
              schoolClassData.brainBreaksCount = brainBreaksCount
              schoolClassData.challengesCount = challengesCount
              schoolClassData.checkInsCount = checkInsCount
              schoolClassData.latestMoodAverage = latestMoodAverage
              schoolClassData.moodAverage = moodAverage
              schoolClassData.topNegativeScoreFactor = this.getScoreFactor(digg(calculationsForSchoolClass, "top_negative_score_factor"))
              schoolClassData.topPositiveScoreFactor = this.getScoreFactor(digg(calculationsForSchoolClass, "top_positive_score_factor"))
              schoolClassData.unicornEffectsCount = unicornEffectsCount

              classStepData.activeCourses += activeCourses
              classStepData.brainBreaksCount += brainBreaksCount
              classStepData.challengesCount += challengesCount
              classStepData.checkInsCount += checkInsCount
              classStepData.unicornEffectsCount += unicornEffectsCount

              schoolData.activeCourses += activeCourses
              schoolData.brainBreaksCount += brainBreaksCount
              schoolData.challengesCount += challengesCount
              schoolData.checkInsCount += checkInsCount
              schoolData.unicornEffectsCount += unicornEffectsCount

              schoolGroupData.activeCourses += activeCourses
              schoolGroupData.brainBreaksCount += brainBreaksCount
              schoolGroupData.challengesCount += challengesCount
              schoolGroupData.checkInsCount += checkInsCount
              schoolGroupData.unicornEffectsCount += unicornEffectsCount

              if (moodAverage) { // eslint-disable-line max-depth
                schoolGroupData.moods.push(moodAverage)
                schoolData.moods.push(moodAverage)
                classStepData.moods.push(moodAverage)
              }

              if (latestMoodAverage) { // eslint-disable-line max-depth
                schoolGroupData.latestMoods.push(latestMoodAverage)
                schoolData.latestMoods.push(latestMoodAverage)
                classStepData.latestMoods.push(latestMoodAverage)
              }
            }
          }
        }
      }

      this.setState({data})
    })
  }

  onDatePickerSelect = ({fromDate, toDate}) => {
    Params.changeParams({
      date_from: I18n.strftime("%Y-%m-%d", fromDate),
      date_to: I18n.strftime("%Y-%m-%d", toDate)
    })
  }

  schoolsForGroup(schoolGroup) {
    const schools = schoolGroup
      .childrenRelationships()
      .loaded()
      .filter((childRelationship) => childRelationship.contactRelationshipType().identifier() == "school_group")
      .map((childRelationship) => {
        const school = childRelationship.child()?.school()

        return school && (this.schoolsById[school.id()] || school)
      })
      .filter((school) => Boolean(school))

    return sortedByFunction(schools, "displayName")
  }

  teachersForSchoolClass(schoolClass) {
    return schoolClass.teachers().loaded()
  }

  onUpgradeOverlayRequestClose = ({reason} = {}) => {
    if (reason !== "stay-basic") {
      return
    }

    if (this.tt.anythingPro === false) {
      this.goBackOrDashboard()
    }
  }

  goBackOrDashboard() {
    const history = ApiMakerConfig.getHistory()

    if (history?.back) {
      history.back()

      return
    }

    if (history?.goBack) {
      history.goBack()

      return
    }

    if (history?.go && typeof history.length === "number" && history.length > 1) {
      history.go(-1)

      return
    }

    if (typeof window !== "undefined" && window.history?.length > 1) {
      window.history.back()

      return
    }

    AppHistory.push(Routes.userRootPath())
  }

  updateExpanded = async ({expanded, identifier, modelClass, modelId}) => {
    let classStatusExpansion = await ClassStatusExpansion
      .ransack({identifier_eq: identifier, resource_id_eq: modelId, resource_type_eq: modelClass, user_id_eq: this.tt.currentUser.id()})
      .first()

    if (!classStatusExpansion) {
      classStatusExpansion = new ClassStatusExpansion({
        identifier,
        resource_id: modelId,
        resource_type: modelClass,
        user_id: this.tt.currentUser.id()
      })
    }

    classStatusExpansion.assignAttributes({expanded})
    await classStatusExpansion.save()
  }
}

export default memo(shapeComponent(RoutesUserClassStatus))
